import React, { useEffect, useState } from 'react'
import { Button, Col, Form, Modal, Row } from 'react-bootstrap'
import DatePicker from 'react-datepicker'
import Select from 'react-select'
import { format } from 'date-fns'

import CustomerLocation from '@data/locations/model/customerLocation'
import {
  useAddDeviceToLocationMutation,
  useCanReadProdDevices,
  useStagedDevicesQuery,
  useTimezonesQuery,
} from '@data/device/device'
import {
  NewDevice,
  StagedDevice,
} from '@data/device/model/device'
import {
  ASSIGNABLE_DEVICE_STATES,
  AssignableDeviceState,
  DEVICE_STATE_LABELS,
} from '@data/device/model/deviceState'
import { TIMEZONE_ACCESS_DENIED_MESSAGE } from '@components/general/DeviceAccessNotice/constants'
import {
  CURRENCY_OPTIONS,
  SECTOR_TYPES_OPTIONS,
  UPLOAD_FREQ_MINS,
  OptionsMap,
  UOM_OPTS
} from '../../DeviceManagement/DeviceInformationManager/constants'
import { pulseOptions } from '@common/utils/helperFunctions'
import { TZDateTimeOverlay } from '@components/ui/Popovers'
import { excludeTimes } from '@components/main/Components/Calendar/DateRangeSelector'

import './Modal.scss'
import '@components/main/Components/Calendar/TimeSelector.css'


interface IModalProps {
  show: boolean
  customerLocation: CustomerLocation
  onHide: () => void
}

const AddDeviceModal: React.FC<IModalProps> = ({
  show,
  customerLocation,
  onHide,
}: IModalProps) => {
  const [selectedDevice, setSelectedDevice] = useState<StagedDevice>()
  const {
    data: stagedDevices = [],
    isPending,
    isError,
    refetch,
  } = useStagedDevicesQuery()
  const {
    data: timezones = [],
    isPending: timezonesPending,
    isError: timezonesError,
    refetch: refetchTimezones,
  } = useTimezonesQuery()
  const canReadProdDevices = useCanReadProdDevices()

  // A disabled query stays `isPending` forever, and the New Device button gates on
  // UPDATE:CUST_LOCATION:DEVICE and READ:DEVICE:STGD — neither of which implies the
  // READ:DEVICE:PROD this query needs — so without this the dropdown would spin
  // indefinitely for a user holding the create scopes but not the read one.
  const isLoadingTimezones = canReadProdDevices && timezonesPending

  const [newDeviceName, setNewDeviceName] = useState('')
  const [newPulses, setNewPulses] = useState(0)
  const [newCost, setNewCost] = useState(2.2)
  const [newDeviceLocationRef, setNewDeviceLocationRef] = useState('')
  const [newDeviceSectorType, setNewSectorType] = useState('')
  const [newWaterType, setNewWaterType] = useState<string>('Cold')
  const [newDeviceState, setNewDeviceState] = useState<AssignableDeviceState>('pendingInstall')
  const [newTimezone, setNewTimezone] = useState<string>('UTC')
  const [newCurrency, setNewCurrency] = useState('')
  const [newInstallDate, setInstallDate] = useState<Date | null>(new Date())
  const [newUploadFreqMins, setNewUploadFreqMins] = useState<number>(0)
  const [newUOM, setNewUom] = useState<string>('')

  const { mutate: addDeviceToLocation } = useAddDeviceToLocationMutation()
  let required =
    !selectedDevice ||
    newDeviceName.length === 0 ||
    newCost === 0 ||
    isNaN(newCost) ||
    newDeviceSectorType === '' ||
    newPulses === 0 ||
    newUploadFreqMins === 0 ||
    newUOM === ''

  useEffect(() => {
    if (show) {
      setSelectedDevice(undefined)
      setNewDeviceName('')
      setNewDeviceLocationRef('')
      setNewPulses(0)
      setNewSectorType('')
      setNewDeviceState('pendingInstall')
      setNewCurrency(OptionsMap[customerLocation.country].currency || '')
      setNewTimezone(OptionsMap[customerLocation.country].timezone || '')
      setNewUom(OptionsMap[customerLocation.country].uom || '')
      setNewCost(OptionsMap[customerLocation.country].unitCost || 2.2)
    }
  }, [show])
  // const tzDate = format(new TZDate(new Date(), 'Europe/Dublin'), 'dd/MM/yyyy HH:mm');

  function handleSave() {
    if (!selectedDevice) return

    const newDevice: NewDevice = {
      deviceId: selectedDevice.deviceId,
      deviceName: newDeviceName.trim(),
      currency: newCurrency,
      installDate: format(newInstallDate ? newInstallDate : new Date(), "yyyy-MM-dd'T'HH:mm:ssxxx"),
      cost: newCost,
      dataAcqMode: 0,
      serverPulseControl: true,
      location: newDeviceLocationRef.trim(),
      timezone: newTimezone,
      sectorType: newDeviceSectorType,
      deviceState: newDeviceState,
      hot: newWaterType === 'Hot',
      uploadFreqMins: newUploadFreqMins,
      uom: newUOM,
      co2Rate: 10.6,
      pulsesPerLiter: newPulses,
      vacant: false
    }
    if (customerLocation.id) {
      addDeviceToLocation({
        device: newDevice,
        locationId: customerLocation.id,
      })
    }
    onHide()
  }

  return (
    <Modal
      onHide={onHide}
      show={show}
      backdrop="static"
      size="lg"
      aria-labelledby="contained-modal-title-vcenter"
      centered
      className="device-information-modal"
    >
      <Modal.Header className="modal-header" closeButton>
        <Modal.Title id="contained-modal-title-vcenter">Add New Device to Account</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {required ? <span className="red">* Required!!</span> : ''}
        <Form className="device-information-form">
          <Row className="mb-3">
            <Form.Group as={Col} controlId="formGridName">
              <Form.Label>
                Staged Device: <span className="red">{!selectedDevice ? '*' : ''}</span>
              </Form.Label>
              <Select
                classNamePrefix="select"
                value={selectedDevice}
                onChange={(d: any) => setSelectedDevice(d)}
                options={stagedDevices}
                getOptionValue={(option) => option.deviceId}
                getOptionLabel={(option) => `${option.deviceName}`}
                isClearable={false}
                isLoading={isPending}
                isDisabled={isPending || isError}
                placeholder={isPending ? 'Loading staged devices…' : 'Select…'}
                noOptionsMessage={() => 'No staged devices available'}
              />
              {isError ? (
                <div className="mt-2">
                  <span className="text-danger">Could not load staged devices.</span>
                  <Button
                    variant="link"
                    size="sm"
                    onClick={() => void refetch()}
                  >
                    Retry
                  </Button>
                </div>
              ) : null}
            </Form.Group>
          </Row>
          <Row className="mb-3">
            <Form.Group as={Col} controlId="formGridName">
              <Form.Label>
                Name <span className="red">{newDeviceName.length === 0 ? '*' : ''}</span>
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter a name"
                value={newDeviceName}
                onChange={(e) => setNewDeviceName(e.target.value)}
              />
            </Form.Group>
            <Form.Group as={Col} controlId="formGridName">
              <Form.Label>Location Reference</Form.Label>
              <Form.Control
                type="text"
                placeholder="Pole B12"
                value={newDeviceLocationRef}
                onChange={(e) => setNewDeviceLocationRef(e.target.value)}
              />
            </Form.Group>
          </Row>

          <Row className="mb-3">
            <Form.Group as={Col} controlId="formGridUOM">
              <Form.Label>
                Unit Of Measurement <span className="red">{!newUOM ? '*' : ''}</span>
              </Form.Label>
              <Select
                classNamePrefix="select"
                value={{
                  value: newUOM,
                  label: newUOM,
                }}
                onChange={(e: any) => setNewUom(e.value)}
                options={UOM_OPTS.map((uom) => {
                  return { value: uom, label: uom }
                })}
                getOptionValue={(option) => option.value}
                getOptionLabel={(option) => `${option.label}`}
                isClearable={false}
              />
            </Form.Group>

            <Form.Group as={Col} controlId="formGridPulses">
              <Form.Label>
                Pulses <span className="red">{!newPulses ? '*' : ''}</span>
              </Form.Label>
              <Select
                classNamePrefix="select"
                onChange={(e: any) => setNewPulses(Number(e.value))}
                options={newUOM ? pulseOptions(newUOM) : []}
                getOptionValue={(option) => option.value}
                getOptionLabel={(option) => `${option.label}`}
                isClearable={false}
                isDisabled={!newUOM}
              />
            </Form.Group>
          </Row>
          <Row className="mb-3">
            <Form.Group as={Col} controlId="formGridCost">
              <Form.Label>
                Cost <span className="red">{newCost === 0 || isNaN(newCost) ? '*' : ''}</span>
              </Form.Label>
              <Form.Control
                type="number"
                placeholder="e.g. 1.93"
                min={0}
                value={newCost}
                onChange={(e) => setNewCost(parseFloat(e.target.value))}
              />
            </Form.Group>
            <Form.Group as={Col} controlId="formGridSpacer"></Form.Group>
          </Row>
          <Row className="mb-3">
            <Form.Group as={Col} controlId="formSectorType">
              <Form.Label>
                Sector <span className="red">{!newDeviceSectorType ? '*' : ''}</span>
              </Form.Label>
              <Select
                classNamePrefix="select"
                onChange={(d: any) => setNewSectorType(d.value)}
                options={SECTOR_TYPES_OPTIONS.map((sector) => {
                  return { value: sector, label: sector }
                })}
                getOptionValue={(option) => option.value}
                getOptionLabel={(option) => `${option.label}`}
                isClearable={false}
              />
            </Form.Group>

            <Form.Group as={Col} controlId="formSectorType">
              <Form.Label>Water Type </Form.Label>
              <Select
                classNamePrefix="select"
                defaultValue={{
                  value: newWaterType,
                  label: newWaterType,
                }}
                onChange={(d: any) => setNewWaterType(d.value)}
                options={['Cold', 'Hot'].map((wType) => {
                  return { value: wType, label: wType }
                })}
                getOptionValue={(option) => option.value}
                getOptionLabel={(option) => `${option.label}`}
                isClearable={false}
              />
            </Form.Group>
          </Row>

          <Row className="mb-3">
            <Form.Group
              as={Col}
              controlId='formDeviceState'
            >
              <Form.Label>Device State</Form.Label>
              <Select
                classNamePrefix='select'
                value={{
                  value: newDeviceState,
                  label: DEVICE_STATE_LABELS[newDeviceState],
                }}
                onChange={(option) => option && setNewDeviceState(option.value)}
                options={ASSIGNABLE_DEVICE_STATES.map((state) => ({
                  value: state,
                  label: DEVICE_STATE_LABELS[state],
                }))}
                isClearable={false}
              />
            </Form.Group>
            {/*<Form.Group as={Col} controlId="formGridOccupants">*/}
            {/*  <Form.Label>Occupants</Form.Label>*/}
            {/*  <Form.Control*/}
            {/*    type="number"*/}
            {/*    value={newOccupants}*/}
            {/*    min={0}*/}
            {/*    onChange={(e) => setNewOccupants(parseInt(e.target.value))}*/}
            {/*  />*/}
            {/*</Form.Group>*/}
          </Row>

          <Row className="mb-3">
            <Form.Group as={Col} controlId="formTimezone">
              <Form.Label>Timezone</Form.Label>
              <Select
                classNamePrefix="select"
                value={{
                  value: newTimezone,
                  label: newTimezone,
                }}
                onChange={(d: any) => setNewTimezone(d.value)}
                options={timezones.map((timezone) => {
                  return { value: timezone.name, label: timezone.name }
                })}
                getOptionValue={(option) => option.value}
                getOptionLabel={(option) => `${option.label}`}
                isClearable={false}
                isLoading={isLoadingTimezones}
                isDisabled={isLoadingTimezones || timezonesError || !canReadProdDevices}
                placeholder={isLoadingTimezones ? 'Loading timezones…' : 'Select…'}
              />
              {!canReadProdDevices ? (
                <div
                  className="text-muted small mt-1"
                  role="status"
                >
                  {TIMEZONE_ACCESS_DENIED_MESSAGE}
                </div>
              ) : null}
              {timezonesError ? (
                <div className="mt-2">
                  <span className="text-danger">Could not load timezones.</span>
                  <Button
                    variant="link"
                    size="sm"
                    onClick={() => void refetchTimezones()}
                  >
                    Retry
                  </Button>
                </div>
              ) : null}
            </Form.Group>
            <Form.Group as={Col} controlId="formGridCurrency">
              <Form.Label>Currency</Form.Label>
              <Select
                classNamePrefix="select"
                value={{
                  value: newCurrency,
                  label: newCurrency,
                }}
                onChange={(d: any) => setNewCurrency(d.value)}
                options={CURRENCY_OPTIONS.map((currency) => {
                  return { value: currency, label: currency }
                })}
                getOptionValue={(option) => option.value}
                getOptionLabel={(option) => `${option.label}`}
                isClearable={false}
              />
            </Form.Group>
          </Row>

          <Row>
            <Form.Group as={Col} controlId="formInstallDate">
              <Form.Label>
                Install Date
                {newInstallDate ?
                  <TZDateTimeOverlay
                    timestamp={newInstallDate}
                    timezone={newTimezone}
                    title={'Install Date Timezone'}
                  /> : null }
              </Form.Label>
              <div>
                <DatePicker
                  selected={newInstallDate}
                  onChange={(date: Date | null) => setInstallDate(date)}
                  placeholderText="Select Install date"
                  showTimeSelect
                  dateFormat="dd/MM/yyyy HH:00"
                  timeFormat="HH:mm"
                  showWeekNumbers
                  excludeTimes={excludeTimes}
                />
              </div>
            </Form.Group>
            <Form.Group as={Col} controlId="formGridUploadFreq">
              <Form.Label>
                Upload Frequency (hours)
                <span className="red">{newUploadFreqMins === 0 ? '*' : ''}</span>
              </Form.Label>
              <Select
                classNamePrefix="select"
                onChange={(d: any) => setNewUploadFreqMins(d.value)}
                options={UPLOAD_FREQ_MINS.map((freq) => {
                  return { value: freq, label: freq }
                })}
                getOptionValue={(option) => `${option.value}`}
                getOptionLabel={(option) => `${parseInt(option.label) / 60}`}
                isClearable={false}
              />
            </Form.Group>
          </Row>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button className="btn btn-secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button disabled={required} onClick={() => handleSave()}>
          Activate Device
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default AddDeviceModal
